import { useCallback, useEffect, useRef } from 'react';
import { extractSubformData } from '@iqvia-technology-solutions/formcrafter';
import {
  getEffectiveValues,
  getEffectiveFieldType,
  hasConditions,
  buildDependencyGraph,
  getTransitiveDependents,
  parseSubformSchema,
  canEditFieldType,
} from '../../../utils/formCrafter/listViewUtils';
import { isFieldVisible } from '../../../utils/formCrafter/visibilityUtils';

/**
 * useListViewCascadeEditing Hook
 *
 * Manages cascade editing for dependent conditional fields in FormCrafterListView.
 * When a field is edited that has dependent conditional fields, this hook:
 * - Automatically opens editing for visible dependent fields
 * - Automatically cancels editing for fields that become hidden
 * - Tracks explicitly cancelled fields to prevent auto-restart
 *
 * @param {Object} options - Hook options
 * @param {Object} options.editing - Field editing hook instance
 * @param {Array} options.subformsToRender - Subforms to render
 * @param {Object} options.formData - Parsed form data
 * @param {string|number} options.formId - Resolved form ID
 * @param {boolean} options.showHiddenFields - Whether hidden fields are shown (reviewer view)
 * @param {boolean} options.enableFieldEdit - Whether field editing is enabled
 * @param {Function} options.canEdit - Function to check if field type can be edited
 * @param {Function} options.isFileFieldType - Function to check if field is file type
 * @returns {Object} - Cascade editing handlers
 */
const useListViewCascadeEditing = ({
  editing,
  subformsToRender,
  formData,
  formId,
  showHiddenFields,
  enableFieldEdit,
  isFileFieldType,
}) => {
  // Track fields explicitly cancelled by user (to avoid auto-restarting)
  const explicitlyCancelledRef = useRef(new Set());

  // Build dependency graphs per subform (memoized in component)
  const getDependencyGraph = useCallback(
    (subformId) => {
      const subform = subformsToRender.find((s) => s?.id === subformId);
      if (!subform) return null;
      const schema = parseSubformSchema(subform);
      return schema?.fields ? buildDependencyGraph(schema.fields) : null;
    },
    [subformsToRender]
  );

  // Get field schema by ID from a subform
  const getFieldById = useCallback(
    (subformId, fieldId) => {
      const subform = subformsToRender.find((s) => s?.id === subformId);
      if (!subform) return null;
      const schema = parseSubformSchema(subform);
      return schema?.fields?.find((f) => f.id === fieldId) || null;
    },
    [subformsToRender]
  );

  // Check if a field can be edited
  const canEditField = useCallback(
    (field) => {
      if (!field) return false;
      const effectiveType = getEffectiveFieldType(field);
      return canEditFieldType(
        effectiveType,
        enableFieldEdit,
        isFileFieldType,
        field
      );
    },
    [enableFieldEdit, isFileFieldType]
  );

  // Handle starting edit with cascade for dependent conditional fields
  const handleStartEditWithCascade = useCallback(
    (subformId, fieldId, initialValue, subformData) => {
      const rootKey = editing.getFieldKey(subformId, fieldId);
      if (!rootKey) return;

      // Clear any previously cancelled fields when starting a new edit
      explicitlyCancelledRef.current.clear();

      // Start editing the clicked field
      editing.startEdit(subformId, fieldId, initialValue);

      // Only cascade in applicant view (not reviewer)
      if (showHiddenFields || !enableFieldEdit) return;

      // Get dependency graph for this subform
      const depGraph = getDependencyGraph(subformId);
      if (!depGraph) return;

      // Find all transitive dependents
      const transitiveDeps = getTransitiveDependents(fieldId, depGraph);
      if (transitiveDeps.size === 0) return;

      // Compute effective values to check visibility
      const effectiveValues = getEffectiveValues(
        subformData,
        editing.editedValues,
        { ...editing.editingDrafts, [rootKey]: initialValue },
        formId,
        subformId
      );

      // Start editing visible, editable dependents
      const fieldsToEdit = [];
      const dependentKeys = [];

      transitiveDeps.forEach((depFieldId) => {
        const depField = getFieldById(subformId, depFieldId);
        if (!depField) return;

        // Check if dependent is visible and editable
        if (!isFieldVisible(depField, effectiveValues)) return;
        if (!canEditField(depField)) return;

        const depKey = editing.getFieldKey(subformId, depFieldId);
        if (!depKey) return;

        // Skip if already editing
        if (editing.isEditing(subformId, depFieldId)) return;

        fieldsToEdit.push({
          subformId,
          fieldId: depFieldId,
          initialValue: effectiveValues[depFieldId] ?? '',
        });
        dependentKeys.push(depKey);
      });

      if (fieldsToEdit.length > 0) {
        editing.startEditBatch(fieldsToEdit);
        editing.registerCascade(rootKey, dependentKeys);
      }
    },
    [
      editing,
      showHiddenFields,
      enableFieldEdit,
      getDependencyGraph,
      formId,
      getFieldById,
      canEditField,
    ]
  );

  // Handle cancel edit with cascade cleanup
  const handleCancelEditWithCascade = useCallback(
    (subformId, fieldId) => {
      const key = editing.getFieldKey(subformId, fieldId);

      // Mark as explicitly cancelled so effect doesn't restart it
      if (key) {
        explicitlyCancelledRef.current.add(key);
      }

      // Cancel the field's edit
      editing.cancelEdit(subformId, fieldId);

      // If this was a cascade root, cancel all its dependents
      if (key && editing.isCascadeRoot(key)) {
        const dependents = editing.getCascadeDependents(key);
        if (dependents.size > 0) {
          // Also mark dependents as cancelled
          dependents.forEach((depKey) =>
            explicitlyCancelledRef.current.add(depKey)
          );
          editing.cancelEditBatch(Array.from(dependents));
        }
        editing.clearCascade(key);
        // Clear the cancelled tracking since cascade is done
        explicitlyCancelledRef.current.clear();
      }
    },
    [editing]
  );

  // Handle confirm edit with cascade cleanup
  const handleConfirmEditWithCascade = useCallback(
    (subformId, fieldId) => {
      const key = editing.getFieldKey(subformId, fieldId);

      // Confirm the field's edit
      editing.confirmEdit(subformId, fieldId);

      // Clear cascade tracking (dependents keep their own edit state)
      if (key) {
        editing.clearCascade(key);
      }
    },
    [editing]
  );

  // Effect to manage cascade:
  // - Cancel editing AND reset values for fields that become hidden
  // - Start editing for fields that become visible while parent is editing
  useEffect(() => {
    if (showHiddenFields || !enableFieldEdit) return;

    const editingKeys = editing.getEditingKeys();
    if (editingKeys.length === 0) return;

    // Track fields to reset (those becoming hidden)
    const fieldsToReset = [];

    // Process each subform
    subformsToRender.forEach((subform) => {
      if (!subform?.id) return;

      const prefix = `${formId}_${subform.id}_`;
      const subformData = extractSubformData(formData, subform.id);
      const effectiveValues = getEffectiveValues(
        subformData,
        editing.editedValues,
        editing.editingDrafts,
        formId,
        subform.id
      );

      // Check visibility of currently editing fields
      editingKeys.forEach((key) => {
        if (!key.startsWith(prefix)) return;

        const fieldId = key.slice(prefix.length);
        const field = getFieldById(subform.id, fieldId);
        if (!field) return;

        // If field is no longer visible, cancel its edit and mark for reset
        if (!isFieldVisible(field, effectiveValues)) {
          editing.cancelEdit(subform.id, fieldId);
          // Add to reset list along with all its transitive dependents
          fieldsToReset.push({ subformId: subform.id, fieldId });

          // Also reset all transitive dependents of this field
          const depGraph = getDependencyGraph(subform.id);
          if (depGraph) {
            const transitiveDeps = getTransitiveDependents(fieldId, depGraph);
            transitiveDeps.forEach((depFieldId) => {
              fieldsToReset.push({
                subformId: subform.id,
                fieldId: depFieldId,
              });
            });
          }
        }
      });

      // Check if dependent fields should start editing
      const depGraph = getDependencyGraph(subform.id);
      if (!depGraph) return;

      editingKeys.forEach((rootKey) => {
        if (!rootKey.startsWith(prefix)) return;

        const rootFieldId = rootKey.slice(prefix.length);
        const transitiveDeps = getTransitiveDependents(rootFieldId, depGraph);

        transitiveDeps.forEach((depFieldId) => {
          const depField = getFieldById(subform.id, depFieldId);
          if (!depField) return;

          const depKey = editing.getFieldKey(subform.id, depFieldId);
          const isDepVisible = isFieldVisible(depField, effectiveValues);
          const isDepEditing = editing.isEditing(subform.id, depFieldId);

          // If dependent is not visible, reset its value
          if (!isDepVisible) {
            fieldsToReset.push({ subformId: subform.id, fieldId: depFieldId });
            return;
          }

          // Skip if already editing or explicitly cancelled/confirmed
          if (isDepEditing) return;

          // Only consider "confirmed" if it has a non-empty value
          // Empty string means it was reset, not user-confirmed
          const wasJustConfirmed =
            Object.prototype.hasOwnProperty.call(
              editing.editedValues,
              depKey
            ) && editing.editedValues[depKey] !== '';
          const wasExplicitlyCancelled =
            explicitlyCancelledRef.current.has(depKey);

          if (wasJustConfirmed || wasExplicitlyCancelled) return;

          // Start editing if field is editable
          if (canEditField(depField)) {
            editing.startEdit(
              subform.id,
              depFieldId,
              effectiveValues[depFieldId] ?? ''
            );
          }
        });
      });
    });

    // Reset values for fields that became hidden (deduplicated)
    if (fieldsToReset.length > 0) {
      const uniqueFieldsToReset = fieldsToReset.filter(
        (field, index, self) =>
          index ===
          self.findIndex(
            (f) =>
              f.subformId === field.subformId && f.fieldId === field.fieldId
          )
      );
      editing.resetFieldValueBatch(uniqueFieldsToReset);
    }
  }, [
    editing.editingDrafts,
    editing.editedValues,
    showHiddenFields,
    enableFieldEdit,
    subformsToRender,
    formId,
    formData,
    getFieldById,
    getDependencyGraph,
    editing,
    canEditField,
  ]);

  // Build edit actions for a field (with cascade support)
  const buildEditActions = useCallback(
    (subformId, fieldId, subformData) => ({
      onStart: (initialValue) =>
        handleStartEditWithCascade(
          subformId,
          fieldId,
          initialValue,
          subformData
        ),
      onCancel: () => handleCancelEditWithCascade(subformId, fieldId),
      onConfirm: () => handleConfirmEditWithCascade(subformId, fieldId),
      onUpdate: (value) => editing.updateDraft(subformId, fieldId, value),
    }),
    [
      handleStartEditWithCascade,
      handleCancelEditWithCascade,
      handleConfirmEditWithCascade,
      editing,
    ]
  );

  return {
    buildEditActions,
    getFieldById,
  };
};

export default useListViewCascadeEditing;
